import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen, GraduationCap, ArrowLeft, Loader2, Lock,
  Shield, ChevronRight, FileText, ArrowRight, Clock,
  CheckCircle, Play, Trophy,
} from "lucide-react";
import { toast } from "sonner";
import { useContentProtection } from "@/components/sample-preview/useContentProtection";
import { Timer } from "@/components/sample-preview/Timer";
import { ScoreResults } from "@/components/sample-preview/ScoreResults";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session ID for conversion tracking
const getSessionId = () => {
  let sid = sessionStorage.getItem("quiz_session_id");
  if (!sid) { sid = Math.random().toString(36).slice(2); sessionStorage.setItem("quiz_session_id", sid); }
  return sid;
};
const trackEvent = (eventType, mockId, ctaVariantId) => {
  axios.post(`${API}/sample-mocks/track`, {
    event_type: eventType, mock_id: mockId || null, session_id: getSessionId(),
    cta_variant: ctaVariantId || null,
  }).catch(() => {});
};

// ── Sample Mock List Page ──
function SampleMocksList() {
  const [mocks, setMocks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/sample-mocks`)
      .then(res => setMocks(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="sample-mocks-list-page">
      <div className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6 transition-colors" data-testid="back-to-home-link">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl bg-[#e0482d]/20 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-[#e0482d]" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold">Sample Mock Exams</h1>
          </div>
          <p className="text-slate-400 text-sm max-w-xl">
            Preview our exam quality before signing up. Take timed quizzes and see your score instantly.
          </p>
          <div className="flex items-center gap-2 mt-4">
            <Badge className="bg-white/10 text-white border-0 text-xs"><Shield className="w-3 h-3 mr-1" /> Content Protected</Badge>
            <Badge className="bg-white/10 text-white border-0 text-xs"><Clock className="w-3 h-3 mr-1" /> Timed Practice</Badge>
            <Badge className="bg-white/10 text-white border-0 text-xs"><GraduationCap className="w-3 h-3 mr-1" /> GES Aligned</Badge>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        ) : mocks.length === 0 ? (
          <Card className="rounded-2xl">
            <CardContent className="py-16 text-center">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-bold mb-2">No sample exams available yet</h3>
              <p className="text-sm text-slate-500 mb-6">Check back soon!</p>
              <Button asChild className="bg-[#e0482d] hover:bg-[#c93d25] text-white rounded-xl"><Link to="/">Go to Homepage</Link></Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {mocks.map((mock, i) => (
              <motion.div key={mock.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                <Link to={`/sample-preview/${mock.id}`} data-testid={`sample-card-${mock.id}`}>
                  <Card className="rounded-2xl hover:shadow-lg transition-shadow cursor-pointer group border-slate-200 h-full">
                    <CardContent className="p-5">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mb-3 group-hover:bg-blue-100 transition-colors">
                        <GraduationCap className="w-5 h-5 text-blue-600" />
                      </div>
                      <h3 className="font-bold text-sm mb-1 group-hover:text-[#e0482d] transition-colors">{mock.title}</h3>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="secondary" className="text-[10px]">{mock.subject}</Badge>
                        <Badge variant="outline" className="text-[10px]">{mock.class_level}</Badge>
                        <Badge className={`text-[10px] border-0 ${
                          mock.difficulty === "hard" ? "bg-red-100 text-red-600" :
                          mock.difficulty === "easy" ? "bg-emerald-100 text-emerald-600" :
                          "bg-blue-100 text-blue-600"
                        }`}>{(mock.difficulty || "medium").charAt(0).toUpperCase() + (mock.difficulty || "medium").slice(1)}</Badge>
                      </div>
                      {mock.description && <p className="text-xs text-slate-500 mb-3 line-clamp-2">{mock.description}</p>}
                      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                          <span>{mock.total_questions} questions</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Timed</span>
                        </div>
                        <span className="text-xs text-[#e0482d] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                          Start <ChevronRight className="w-3 h-3" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-10 text-center">
          <p className="text-sm text-slate-500 mb-3">Want access to our full library of mock exams?</p>
          <div className="flex items-center justify-center gap-3">
            <Button asChild className="bg-[#e0482d] hover:bg-[#c93d25] text-white rounded-xl">
              <Link to="/register" onClick={() => trackEvent("signup_clicked")} data-testid="signup-cta">Create Free Account <ArrowRight className="w-4 h-4 ml-1" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-xl"><Link to="/mock-exams">Browse Full Catalog</Link></Button>
            <Button asChild variant="outline" className="rounded-xl">
              <Link to="/my-progress" data-testid="my-progress-link"><Trophy className="w-4 h-4 mr-1.5" /> My Progress</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sample Mock Preview (Timed Practice Mode) ──
function SampleMockPreview() {
  const { id } = useParams();
  const [mock, setMock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [quizState, setQuizState] = useState("ready");
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [scoreData, setScoreData] = useState(null);
  const [timeTaken, setTimeTaken] = useState(0);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  useContentProtection();

  useEffect(() => {
    axios.get(`${API}/sample-mocks/${id}/preview`)
      .then(res => {
        setMock(res.data);
        const t = res.data.time_limit_seconds || res.data.preview_count * 60;
        setTimeLeft(t); setTotalTime(t);
      })
      .catch(() => toast.error("Sample exam not found"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (quizState !== "active") return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); if (window._submitQuiz) window._submitQuiz(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [quizState]);

  const startQuiz = () => {
    setQuizState("active"); setSelectedAnswers({}); setScoreData(null); setTimeTaken(0);
    startTimeRef.current = Date.now();
    const t = mock?.time_limit_seconds || (mock?.preview_count || 3) * 60;
    setTimeLeft(t); setTotalTime(t);
  };

  const handleSubmit = useCallback(async () => {
    clearInterval(timerRef.current);
    const elapsed = startTimeRef.current ? Math.round((Date.now() - startTimeRef.current) / 1000) : 0;
    setTimeTaken(elapsed); setQuizState("submitting");
    try {
      const res = await axios.post(`${API}/sample-mocks/${id}/check`, { answers: selectedAnswers });
      setScoreData(res.data); setQuizState("results"); trackEvent("quiz_completed", id);
      // Track attempt for progress analytics
      axios.post(`${API}/quiz-progress/track`, {
        session_id: getSessionId(),
        mock_id: id,
        mock_title: mock?.title || "",
        subject: mock?.subject || "",
        difficulty: mock?.difficulty || "medium",
        score: res.data.score,
        total: res.data.total,
        percentage: res.data.percentage,
        time_taken_seconds: elapsed,
      }).catch(() => {});
    } catch { toast.error("Failed to check answers"); setQuizState("active"); }
  }, [id, selectedAnswers, mock]);

  useEffect(() => { window._submitQuiz = handleSubmit; }, [handleSubmit]);

  const retry = () => {
    setSelectedAnswers({}); setScoreData(null); setQuizState("ready");
    const t = mock?.time_limit_seconds || (mock?.preview_count || 3) * 60;
    setTimeLeft(t); setTotalTime(t);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  if (!mock) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <BookOpen className="w-12 h-12 mb-4 text-slate-300" />
        <h2 className="text-lg font-bold mb-2">Sample not found</h2>
        <Button asChild variant="outline" className="rounded-xl mt-2"><Link to="/sample-preview">Back to samples</Link></Button>
      </div>
    );
  }

  const answeredCount = Object.keys(selectedAnswers).length;
  const totalQuestions = mock.questions?.length || 0;

  return (
    <div className="min-h-screen bg-slate-50 protected-content" data-testid="sample-mock-preview-page"
      style={{ userSelect: "none", WebkitUserSelect: "none", MozUserSelect: "none" }}>
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <Link to="/sample-preview" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-xs mb-2 transition-colors" data-testid="back-to-samples">
                <ArrowLeft className="w-3 h-3" /> All Samples
              </Link>
              <h1 className="text-lg sm:text-xl font-bold">{mock.title}</h1>
              <div className="flex items-center gap-3 text-sm text-slate-400 mt-1">
                <span>{mock.subject}</span>
                <span>{mock.class_level}</span>
                <Badge className={`border-0 text-[10px] ${
                  mock.difficulty === "hard" ? "bg-red-500/20 text-red-300" :
                  mock.difficulty === "easy" ? "bg-emerald-500/20 text-emerald-300" :
                  "bg-blue-500/20 text-blue-300"
                }`} data-testid="difficulty-badge">{(mock.difficulty || "medium").charAt(0).toUpperCase() + (mock.difficulty || "medium").slice(1)}</Badge>
                <Badge className="bg-amber-500/20 text-amber-300 border-0 text-[10px]">
                  <Lock className="w-2.5 h-2.5 mr-1" /> {mock.preview_count} of {mock.total_questions}
                </Badge>
              </div>
            </div>
            {quizState === "active" && (
              <div className="flex flex-col items-end gap-2">
                <Timer seconds={timeLeft} total={totalTime} />
                <p className="text-xs text-slate-500">{answeredCount}/{totalQuestions} answered</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 relative">
        {/* Watermark */}
        <div className="pointer-events-none fixed inset-0 z-10 flex items-center justify-center overflow-hidden" aria-hidden="true" data-testid="watermark-overlay">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(-45deg, transparent, transparent 200px, rgba(224,72,45,0.04) 200px, rgba(224,72,45,0.04) 201px)`,
          }} />
          <div className="transform -rotate-45 opacity-[0.06] select-none whitespace-nowrap">
            <p className="text-6xl sm:text-8xl font-black tracking-widest text-slate-900">SAMPLE</p>
            <p className="text-lg sm:text-2xl font-bold text-center tracking-[0.3em] text-slate-900 mt-2">GREAT MESSIAH PUBLICATION</p>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {/* Ready State */}
          {quizState === "ready" && (
            <motion.div key="ready" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <Card className="rounded-2xl max-w-lg mx-auto" data-testid="quiz-ready-card">
                <CardContent className="py-10 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-[#e0482d]/10 flex items-center justify-center mx-auto mb-5">
                    <Play className="w-7 h-7 text-[#e0482d]" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2">Ready to Start?</h2>
                  <p className="text-sm text-slate-500 mb-1">{mock.preview_count} questions from {mock.title}</p>
                  <Badge className={`text-xs mb-4 ${
                    mock.difficulty === "hard" ? "bg-red-100 text-red-600" :
                    mock.difficulty === "easy" ? "bg-emerald-100 text-emerald-600" :
                    "bg-blue-100 text-blue-600"
                  }`} data-testid="ready-difficulty">{(mock.difficulty || "medium").charAt(0).toUpperCase() + (mock.difficulty || "medium").slice(1)} Difficulty</Badge>
                  <div className="flex items-center justify-center gap-3 text-xs text-slate-400 mb-6">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {Math.ceil(totalTime / 60)} min time limit</span>
                    <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {mock.preview_count} questions</span>
                  </div>
                  <Button onClick={startQuiz} className="bg-[#e0482d] hover:bg-[#c93d25] text-white rounded-xl h-11 px-8 font-semibold" data-testid="start-quiz-btn">
                    Start Timed Quiz <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <p className="text-xs text-slate-400 mt-4">Timer starts when you click the button</p>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Active Quiz */}
          {(quizState === "active" || quizState === "submitting") && (
            <motion.div key="active" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4 relative z-0">
              {mock.questions.map((q, i) => (
                <motion.div key={q.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
                  <Card className="rounded-2xl border-slate-200 shadow-sm" data-testid={`preview-question-${i}`}>
                    <CardContent className="p-5 sm:p-6">
                      <div className="flex items-start gap-3 mb-4">
                        <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-sm font-bold ${
                          selectedAnswers[q.id] !== undefined ? "bg-emerald-500 text-white" : "bg-[#e0482d]/10 text-[#e0482d]"
                        }`}>{i + 1}</span>
                        <p className="text-sm font-medium text-slate-800 leading-relaxed pt-1">{q.question}</p>
                      </div>
                      {q.options?.length > 0 && (
                        <div className="space-y-2 ml-11">
                          {q.options.map((opt, oi) => {
                            const isSelected = selectedAnswers[q.id] === oi;
                            return (
                              <button key={oi}
                                onClick={() => setSelectedAnswers(prev => ({ ...prev, [q.id]: oi }))}
                                disabled={quizState === "submitting"}
                                className={`w-full text-left px-4 py-2.5 rounded-xl text-sm border transition-all ${
                                  isSelected ? "border-[#e0482d] bg-[#e0482d]/5 text-[#e0482d] font-medium"
                                    : "border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700"
                                }`} data-testid={`option-${i}-${oi}`}>
                                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full mr-3 text-xs font-bold ${
                                  isSelected ? "bg-[#e0482d] text-white" : "bg-slate-100 text-slate-500"
                                }`}>{String.fromCharCode(65 + oi)}</span>
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
              <div className="flex items-center justify-between pt-4 pb-2">
                <p className="text-xs text-slate-500">{answeredCount} of {totalQuestions} answered</p>
                <Button onClick={handleSubmit} disabled={quizState === "submitting"}
                  className="bg-[#e0482d] hover:bg-[#c93d25] text-white rounded-xl" data-testid="submit-quiz-btn">
                  {quizState === "submitting" ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Checking...</>
                    : <>Submit Answers <CheckCircle className="w-4 h-4 ml-1.5" /></>}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Results */}
          {quizState === "results" && scoreData && (
            <motion.div key="results" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <ScoreResults
                score={scoreData.score} total={scoreData.total}
                percentage={scoreData.percentage} results={scoreData.results}
                questions={mock.questions} onRetry={retry}
                mockId={id} timeTaken={timeTaken} trackEvent={trackEvent}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Locked Content Teaser */}
        {mock.total_questions > mock.preview_count && quizState !== "results" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-6">
            <Card className="rounded-2xl border-dashed border-2 border-slate-300 bg-slate-50/50" data-testid="locked-content-teaser">
              <CardContent className="py-8 text-center">
                <Lock className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                <h3 className="font-bold text-slate-700 mb-1">{mock.total_questions - mock.preview_count} more questions available</h3>
                <p className="text-sm text-slate-500 mb-4">Sign up to access the full library</p>
                <Button asChild className="bg-[#e0482d] hover:bg-[#c93d25] text-white rounded-xl">
                  <Link to="/register" onClick={() => trackEvent("signup_clicked", id)} data-testid="preview-signup-btn">Create Free Account <ArrowRight className="w-4 h-4 ml-1" /></Link>
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        <div className="mt-6 flex items-center gap-2 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs">
          <Shield className="w-4 h-4 shrink-0" />
          <span>This content is protected. Copying, screenshots, and screen recording are deterred. All content is watermarked.</span>
        </div>
      </div>
    </div>
  );
}

export default function SamplePreviewPage() {
  const { id } = useParams();
  return id ? <SampleMockPreview /> : <SampleMocksList />;
}
